import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody, Spinner, useTabParam } from '@structural-climbing/ui'
import {
  competitionBoardTabs,
  type CompetitionBoard,
  type CompetitionDiscipline,
} from '@structural-climbing/validation'
import { ScoringCard } from '../standings/ScoringCard'
import { StandingsTable } from '../standings/StandingsTable'
import { BoardTabs } from './BoardTabs'
import { boardEntries } from './boards'
import { publicSiteUrl } from './publicSite'
import { useCompetitionStandings } from './useCompetitionStandings'
import type { CompetitionCategoryView } from './types'

interface Props {
  competitionId: string
  /** Used to link out to the public board, which carries every category. */
  slug: string
  discipline: CompetitionDiscipline
  myUserId: string | undefined
  /** This competition's own categories, in the order the gym set. */
  categories: CompetitionCategoryView[]
  myCategoryId: string | null
  apiUrl: string
  getAccessTokenSilently: () => Promise<string>
}

/**
 * The board for the competitor's OWN category, and nothing else.
 *
 * Browsing the other categories belongs on the public site: in here a
 * competitor is climbing, and the question is where they stand among the people
 * they are actually ranked against. The category tabs invited a comparison that
 * the scoring does not make, since each category splits its own 1000 points.
 */
export function CompetitionStandingsTab({
  competitionId,
  slug,
  discipline,
  myUserId,
  categories,
  myCategoryId,
  apiUrl,
  getAccessTokenSilently,
}: Props) {
  const { t } = useTranslation()
  // Their own category. Someone reading without an entry falls back to the
  // first, which is the same category the board opened on before.
  const categoryId = myCategoryId ?? categories[0]?.id ?? 'all'
  const category = categories.find((c) => c.id === categoryId)
  // General, bouldering or lead. Only switchable when the competition is
  // combined, where it opens on General. In the URL, so a refresh stays on the
  // lead board.
  const boards = competitionBoardTabs(discipline)
  const [board, setBoard] = useTabParam<CompetitionBoard>('board', boards, boards[0])

  const { data, loading, reload } = useCompetitionStandings(
    competitionId,
    categoryId,
    apiUrl,
    getAccessTokenSilently,
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 px-1">
        <p className="text-sm font-semibold">{category?.name ?? t('competitions.tabStandings')}</p>
        {/* Scores move as other people top routes, and a competitor checks the
            board mid-session rather than reloading the whole page. */}
        <Button
          size="sm"
          variant="flat"
          onPress={() => void reload()}
          isLoading={loading}
          startContent={
            loading ? undefined : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="size-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h1.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.389Zm1.219-7.66a.75.75 0 0 0-.75.75v2.43l-.31-.31A7 7 0 0 0 3.759 9.772a.75.75 0 1 0 1.449.389 5.5 5.5 0 0 1 9.201-2.466l.312.311H13.288a.75.75 0 0 0 0 1.5h3.243a.75.75 0 0 0 .75-.75V4.514a.75.75 0 0 0-.75-.75Z"
                  clipRule="evenodd"
                />
              </svg>
            )
          }
        >
          {t('competitions.refreshScores')}
        </Button>
      </div>

      <BoardTabs discipline={discipline} board={board} onChange={setBoard} />

      <Card className="shadow-sm">
        <CardBody>
          {loading && !data ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <StandingsTable
              entries={boardEntries(data, discipline, board)}
              myUserId={myUserId}
              apiUrl={apiUrl}
              // The gym-standings default talks about "this period", which
              // means nothing inside a competition category.
              emptyContent={t('competitions.standingsEmpty')}
            />
          )}
        </CardBody>
      </Card>

      {/* Only worth saying when there is in fact somewhere else to look. */}
      {categories.length > 1 && (
        <p className="px-1 text-xs text-foreground/55">
          {t('competitions.otherCategoriesOnSite')}{' '}
          <a
            href={publicSiteUrl(`/competitions/${slug}`)}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline"
          >
            {t('competitions.seeFullStandings')}
          </a>
        </p>
      )}

      {/* The same card the gym standings show, explaining whichever formula
          this competition is actually scored on. */}
      <ScoringCard
        discipline={board === 'general' ? undefined : board}
        scoringType={data?.scoringType}
        combined={discipline === 'combined'}
        progressLink={null}
      />
    </div>
  )
}
